import {drawDashedLine, drawFigure, colors, Point} from "../utils";
import {Animation, glowPointAnimation} from '../animations';

import moment from "moment";
import * as _ from 'lodash';


 // main chart line with gradient background
export class Chart extends PIXI.Container{
  constructor(App){
    super();

    this.zIndex = 2;

    this.App = App;
    this.background = new ChartBackground();
    this.graphics = new PIXI.Graphics();

    this.lastPoint = new LastPoint();

    this.backgroundMask = new PIXI.Graphics();
    this.backgroundMask.isMask = true;
    this.background.mask = this.backgroundMask;

    this.position.y = App.chartPosition.top;
    this.height = App.chartPosition.height;
    this.width = App.renderer.width/App.devicePixelRatio;

    this.interactive = true;
    this.hitArea = new PIXI.Rectangle(0, 0, this._width, App.renderer.height * 0.75);
    this.on('mousedown', this.mouseDownHandler.bind(this)).on('mouseup', this.mouseUpHandel.bind(this));

    this.addChild(this.background, this.lastPoint, this.backgroundMask, this.graphics);
  }

  // binds mouseEvent to mouseMove handler
  mouseDownHandler(event){
    let App = this.App;

    this.clickTimestamp = event.data.global.x / this._width * (App.endTimestamp -  App.startTimestamp);
    this.oldStartTimestamp = App.startTimestamp;
    this.oldEndTimestamp = App.endTimestamp;
    this.on('mousemove', this.mouseMoveHandel.bind(this));
  }

  // moves chart by cursor
  mouseMoveHandel(event){
    let App = this.App;

    let delta = this.clickTimestamp - event.data.global.x / this._width * (App.endTimestamp - App.startTimestamp);
    if (delta < 0 || _.last(App.points).timestamp - App.startTimestamp > (App.endTimestamp - App.startTimestamp) / 3) {
      App.startTimestamp = this.oldStartTimestamp + delta;
      App.endTimestamp = this.oldEndTimestamp + delta;
    }
    if (event.data.global.x < 0 || event.data.global.x > this._width) this.off('mousemove');
  }

  // unbinds mouseEvent from mouseMove handler
  mouseUpHandel(){
    this.off('mousemove');
  }

  // renders line for each frame
  render(){
    let graphics = this.graphics, renderer = this.App.renderer;
    if (this.App.displayedPoints.length > 1) {
      let firstPoint = this.App.displayedPoints[0], lastPoint = _.last(this.App.displayedPoints), point = _.last(this.App.points);
      let displayedPoints = this.App.displayedPoints;

      let trades = this.App.trades;
      let orders = [], orderPoints = [];

      // adds orderPoints to chart
      if (trades.objects) {
        orders = trades.objects.filter((order) => order.symbol == this.App.quotes.currentSymbol &&
          order.options_data && order.options_data.expiration_time.unix() * 1000 > this.App.initTime &&
          _.inRange(order.options_data.expiration_time.unix() * 1000, this.App.startTimestamp, this.App.endTimestamp)
        );
        for (let order of orders){
          orderPoints.push(new Point(moment(order.open_time).unix() * 1000, order.open_price));
          if (order.isClosed && order.options_data.expiration_time.unix() * 1000 < _.last(this.App.points).timestamp){
            orderPoints.push(new Point(order.options_data.expiration_time.unix() * 1000, order.close_price));
          }
        }
        this.App.recalculatePoints(orderPoints);
      }

      let drawPath = displayedPoints.map((p) => [p.x, p.y]);

      for (let order of orderPoints){
        if (order.timestamp < Date.now()) drawPath.splice(_.sortedIndexBy(drawPath, [order.x, order.y], (point) => point[0]), 0, [order.x, order.y]);
      }

      // draws mask for gradient background
      drawFigure(this.backgroundMask, drawPath.concat([[lastPoint.x, renderer.height], [0, renderer.height], [0, firstPoint.y]]),
        [colors.chart, 0.1], true);

      drawFigure(graphics, drawPath, [1, colors.chart, 1], true);
      if (firstPoint.x > 0){
        drawDashedLine(graphics, -15 + 15 * (Date.now() % 1000) / 1000, firstPoint.y, firstPoint.x, firstPoint.y, 10, 5);
      }

      this.background.backgroundGradient.scale.x = this._width;
      this.background.backgroundGradient.height = this._height + this.position.y;

      // if user can see last point, make it big blinking dot
      if (lastPoint.timestamp == point.timestamp){

        graphics.beginFill(this.App.getColorTrade(), 1).drawCircle(point.x, point.y, 5).endFill();
        this.lastPoint.position.x = lastPoint.x;
        this.lastPoint.position.y = lastPoint.y;
        if (this.App.animations.animations.filter(animation => glowPointAnimation.name == animation.name).length == 0){
          this.App.animations.animations.push(new Animation(glowPointAnimation, 2000, this.App));
        }
      }
    }
    else{
      // if no points to represent, just draws dashed line of screen width
      graphics.clear();
      this.backgroundMask.clear();
      this.backgroundMask.drawRect(0, this._height / 2, this._width, renderer.height);
      graphics.lineStyle(1, 'colors.chart', 1);
      drawDashedLine(graphics, -15 + 15 * (Date.now() % 1000) / 1000, this._height / 2, this._width, this._height / 2, 10, 5);
    }
  }
}


// big white dot in the end of chart line
class LastPoint extends PIXI.Container{
  constructor(){
    super();

    this.pointShadow = new PIXI.Sprite.fromImage('/static/img/webtrader/chart/point_shadow.png');
    this.pointShadow.position.set(-23, -23);
    this.addChild(this.pointShadow);
  }
}


// gradient background
class ChartBackground extends PIXI.Container{
  constructor(){
    super();

    this.backgroundGradient = new PIXI.Sprite.fromImage('/static/img/webtrader/chart/gradient_background.png');
    this.addChild(this.backgroundGradient);
  }
}


// line with white arrow that shows current quote value
export class CurrentValue extends PIXI.Container{
  constructor(App){
    super();

    this.zIndex = 9;
    this.App = App;
    this.arrow = new PIXI.Graphics();

    // draws arrow
    this._drawArrow();

    this.arrowContainer = new PIXI.Container();
    this.graphics = new PIXI.Graphics();

    this.text = new PIXI.Text(gettext('value'));
    this.text.style = {fontSize: '15px', fontFamily: 'Dintext, Arial, Helvetica, sans-serif', fill: colors.currentQuoteText};
    this.text.position.set(App.rightMargin * 0.3, 6);

    this.arrowContainer.addChild(this.arrow, this.text);
    this.arrowContainer.position.x = App.renderer.width/App.devicePixelRatio - App.rightMargin * 1.3;

    this.addChild(this.graphics, this.arrowContainer);
  }

  _drawArrow() {
    drawFigure(this.arrow, [[12, 0], [this.App.rightMargin * 1.3, 0], [this.App.rightMargin * 1.3, 30], [12, 30], [0, 15], [12, 0]],
        [this.App.getColorTrade(), 1]);
  }

  render(){
    let App = this.App;

    if (App.points.length > 0) {
      this.text.text = App.quotes.currentQuote.bid_display;
      let y = App.getY(_.last(App.points).value) + App.chartPosition.top;

      this.arrow.clear();
      this._drawArrow();

      this.arrowContainer.y = y - this.arrowContainer.height / 2;

      this.text.style.fill = this.App.isHoverTrade() ? colors.chart : colors.currentQuoteText;

      this.graphics.clear().lineStyle(1, this.App.getColorTrade(), 1).moveTo(0, y);
      this.graphics.lineTo(App.renderer.width/App.devicePixelRatio - App.rightMargin, y);
    }
  }
}
